import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Package, AlertTriangle, TrendingUp, Minus, Plus, RefreshCw,
  DollarSign, CreditCard, Truck, ClipboardList, Search, Filter,
  ArrowUpDown, Edit2, Save, X, History, Bell, ShoppingCart, Store,
  Wifi, WifiOff, CheckCircle, Loader2, AlertCircle, ChevronDown, ChevronUp,
  Eye, Mail, Phone, MapPin, Copy, Send, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StoreManager = ({ headers }) => {
  const [inventory, setInventory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({ quantity: 0, reason: '' });
  const [showPOSDialog, setShowPOSDialog] = useState(false);
  const [posCart, setPosCart] = useState([]);
  const [posSearchQuery, setPosSearchQuery] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [newSkuValue, setNewSkuValue] = useState('');

  // Credit card surcharge settings
  const [surchargeSettings, setSurchargeSettings] = useState({
    enabled: true,
    percent: 2.9
  });

  // Stripe Terminal State
  const terminalRef = useRef(null);
  const [terminalLoaded, setTerminalLoaded] = useState(false);
  const [connectedReader, setConnectedReader] = useState(null);
  const [discoveredReaders, setDiscoveredReaders] = useState([]);
  const [terminalStatus, setTerminalStatus] = useState('disconnected'); // disconnected, connecting, connected, processing
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentPaymentIntent, setCurrentPaymentIntent] = useState(null);
  const [useSimulator, setUseSimulator] = useState(true); // Default to simulator for testing
  const [posConfigured, setPosConfigured] = useState(null); // null = loading, true/false

  useEffect(() => {
    fetchData();
    checkPOSStatus();
    fetchSurchargeSettings();
  }, []);

  const fetchSurchargeSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      setSurchargeSettings({
        enabled: res.data.credit_card_surcharge_enabled ?? true,
        percent: res.data.credit_card_surcharge_percent ?? 2.9
      });
    } catch (error) {
      // Use defaults
    }
  };

  const checkPOSStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/status`, { headers });
      setPosConfigured(res.data.configured);
    } catch (error) {
      setPosConfigured(false);
    }
  };

  const fetchData = async () => {
    try {
      const [inventoryRes, alertsRes, logsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/api/products/inventory`, { headers }),
        axios.get(`${API_URL}/api/products/inventory/alerts`, { headers }),
        axios.get(`${API_URL}/api/products/inventory/logs?limit=20`, { headers }),
        axios.get(`${API_URL}/api/orders`, { headers })
      ]);
      setInventory(inventoryRes.data.products || []);
      setSummary(inventoryRes.data.summary || {});
      setAlerts(alertsRes.data || []);
      setLogs(logsRes.data || []);
      setOrders(ordersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch store data');
    } finally {
      setLoading(false);
    }
  };

  const updateInventory = async (productId, stockQuantity, threshold, reorderQty) => {
    try {
      await axios.put(`${API_URL}/api/products/inventory/${productId}`, null, {
        headers,
        params: {
          stock_quantity: stockQuantity,
          low_stock_threshold: threshold,
          reorder_quantity: reorderQty
        }
      });
      toast.success('Inventory updated');
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update inventory');
    }
  };

  const adjustInventory = async () => {
    if (!editingProduct || adjustmentData.quantity === 0) return;
    try {
      await axios.post(
        `${API_URL}/api/products/inventory/${editingProduct.id}/adjust`,
        null,
        {
          headers,
          params: {
            quantity_change: adjustmentData.quantity,
            reason: adjustmentData.reason || 'manual_adjustment'
          }
        }
      );
      toast.success('Inventory adjusted');
      setShowAdjustDialog(false);
      setAdjustmentData({ quantity: 0, reason: '' });
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust inventory');
    }
  };

  const updateProductSKU = async (productId, newSKU) => {
    try {
      await axios.put(`${API_URL}/api/products/${productId}`, 
        { sku: newSKU },
        { headers }
      );
      toast.success('SKU updated successfully');
      setEditingSku(null);
      setNewSkuValue('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update SKU');
    }
  };

  const startSkuEdit = (product) => {
    setEditingSku(product.id);
    setNewSkuValue(product.sku || `SKU-${product.id.slice(0, 8).toUpperCase()}`);
  };

  const cancelSkuEdit = () => {
    setEditingSku(null);
    setNewSkuValue('');
  };

  const sendOrderEmail = async () => {
    if (!selectedOrder || !emailSubject || !emailMessage) return;
    
    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/api/orders/${selectedOrder.id}/email`, 
        { 
          subject: emailSubject,
          message: emailMessage,
          to_email: selectedOrder.customer_email
        },
        { headers }
      );
      toast.success(`Email sent to ${selectedOrder.customer_email}`);
      setShowEmailModal(false);
      setSelectedOrder(null);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const openOrderEmail = (order, type = 'update') => {
    setSelectedOrder(order);
    const templates = {
      update: {
        subject: `Order Update - ${order.id.slice(0, 8)}`,
        message: `Hi ${order.customer_name},\n\nWe wanted to give you an update on your order.\n\nOrder ID: ${order.id.slice(0, 8)}\nStatus: ${order.status}\n\n[Your message here]\n\nThank you for choosing D-Freeze!\n\nBest regards,\nD-Freeze Team`
      },
      shipped: {
        subject: `Your Order Has Shipped! - ${order.id.slice(0, 8)}`,
        message: `Hi ${order.customer_name},\n\nGreat news! Your order has been shipped!\n\nOrder ID: ${order.id.slice(0, 8)}\nTracking Number: [Enter tracking number]\n\nYou can track your package at [carrier tracking URL].\n\nThank you for choosing D-Freeze!\n\nBest regards,\nD-Freeze Team`
      },
      delivered: {
        subject: `Your Order Has Been Delivered - ${order.id.slice(0, 8)}`,
        message: `Hi ${order.customer_name},\n\nYour order has been delivered!\n\nOrder ID: ${order.id.slice(0, 8)}\n\nWe hope you enjoy your new freeze dryer! If you have any questions or need assistance, please don't hesitate to reach out.\n\nThank you for choosing D-Freeze!\n\nBest regards,\nD-Freeze Team`
      }
    };
    const template = templates[type] || templates.update;
    setEmailSubject(template.subject);
    setEmailMessage(template.message);
    setShowEmailModal(true);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API_URL}/api/orders/${orderId}/status`, null, {
        headers,
        params: { status }
      });
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // POS Functions
  const addToCart = (product) => {
    const existing = posCart.find(item => item.id === product.id);
    if (existing) {
      setPosCart(posCart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setPosCart([...posCart, { ...product, quantity: 1 }]);
    }
  };

  const updateCartQuantity = (productId, delta) => {
    setPosCart(posCart.map(item => {
      if (item.id === productId) {
        const newQty = Math.max(0, item.quantity + delta);
        return newQty === 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const getCartTotal = () => {
    return posCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getCartSurcharge = () => {
    if (!surchargeSettings.enabled) return 0;
    return Math.round(getCartTotal() * (surchargeSettings.percent / 100) * 100) / 100;
  };

  const getCartTotalWithSurcharge = () => {
    return getCartTotal() + getCartSurcharge();
  };

  // ============== Stripe Terminal Functions ==============
  
  // Initialize Terminal SDK
  const initializeTerminal = useCallback(async () => {
    if (terminalRef.current) return;
    
    try {
      // Dynamically load the Stripe Terminal SDK
      const { loadStripeTerminal } = await import('@stripe/terminal-js');
      const StripeTerminal = await loadStripeTerminal();
      
      const fetchConnectionToken = async () => {
        const response = await axios.post(`${API_URL}/api/pos/connection-token`, {}, { headers });
        return response.data.secret;
      };
      
      terminalRef.current = StripeTerminal.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: () => {
          toast.error('Reader disconnected unexpectedly');
          setConnectedReader(null);
          setTerminalStatus('disconnected');
        }
      });
      
      setTerminalLoaded(true);
      toast.success('Terminal SDK loaded');
    } catch (error) {
      console.error('Failed to load Terminal SDK:', error);
      toast.error('Failed to initialize Terminal');
    }
  }, [headers]);

  // Discover readers
  const discoverReaders = async () => {
    if (!terminalRef.current) {
      await initializeTerminal();
    }
    
    setTerminalStatus('connecting');
    
    try {
      const config = { simulated: useSimulator };
      const discoverResult = await terminalRef.current.discoverReaders(config);
      
      if (discoverResult.error) {
        throw new Error(discoverResult.error.message);
      }
      
      setDiscoveredReaders(discoverResult.discoveredReaders);
      
      if (discoverResult.discoveredReaders.length === 0) {
        toast.info(useSimulator ? 'No simulated readers found' : 'No readers found nearby');
        setTerminalStatus('disconnected');
      } else {
        toast.success(`Found ${discoverResult.discoveredReaders.length} reader(s)`);
      }
    } catch (error) {
      console.error('Discovery failed:', error);
      toast.error('Failed to discover readers');
      setTerminalStatus('disconnected');
    }
  };

  // Connect to a reader
  const connectToReader = async (reader) => {
    if (!terminalRef.current) return;
    
    setTerminalStatus('connecting');
    
    try {
      const connectResult = await terminalRef.current.connectReader(reader);
      
      if (connectResult.error) {
        throw new Error(connectResult.error.message);
      }
      
      setConnectedReader(connectResult.reader);
      setTerminalStatus('connected');
      toast.success(`Connected to ${reader.label || 'reader'}`);
    } catch (error) {
      console.error('Connection failed:', error);
      toast.error('Failed to connect to reader');
      setTerminalStatus('disconnected');
    }
  };

  // Disconnect from reader
  const disconnectReader = async () => {
    if (!terminalRef.current || !connectedReader) return;
    
    try {
      await terminalRef.current.disconnectReader();
      setConnectedReader(null);
      setTerminalStatus('disconnected');
      toast.success('Reader disconnected');
    } catch (error) {
      console.error('Disconnect failed:', error);
    }
  };

  // Process payment with Terminal
  const processTerminalPayment = async () => {
    if (posCart.length === 0) return;
    
    setPaymentStatus('creating');
    
    try {
      // Create order and PaymentIntent on backend
      const orderData = {
        items: posCart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer_name: 'Walk-in Customer',
        reader_id: connectedReader?.id
      };
      
      const orderRes = await axios.post(`${API_URL}/api/pos/create-order`, orderData, { headers });
      setCurrentPaymentIntent(orderRes.data.payment_intent_id);
      
      setPaymentStatus('collecting');
      toast.info('Present card to reader...');
      
      // Collect payment method from reader
      const collectResult = await terminalRef.current.collectPaymentMethod(orderRes.data.client_secret);
      
      if (collectResult.error) {
        throw new Error(collectResult.error.message);
      }
      
      setPaymentStatus('processing');
      
      // Process the payment
      const processResult = await terminalRef.current.processPayment(collectResult.paymentIntent);
      
      if (processResult.error) {
        throw new Error(processResult.error.message);
      }
      
      // Verify payment status
      if (processResult.paymentIntent.status === 'succeeded') {
        // Complete the order on backend
        await axios.post(
          `${API_URL}/api/pos/complete-order/${orderRes.data.payment_intent_id}`,
          {},
          { headers }
        );
        
        setPaymentStatus('success');
        toast.success('Payment successful!');
        
        // Clear cart and close dialog after delay
        setTimeout(() => {
          setPosCart([]);
          setPaymentStatus(null);
          setCurrentPaymentIntent(null);
          fetchData();
        }, 2000);
      } else {
        throw new Error(`Payment status: ${processResult.paymentIntent.status}`);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      setPaymentStatus('failed');
      toast.error(error.message || 'Payment failed');
      
      // Cancel the payment intent if it exists
      if (currentPaymentIntent) {
        try {
          await axios.post(`${API_URL}/api/pos/cancel-payment/${currentPaymentIntent}`, {}, { headers });
        } catch (e) {
          console.error('Failed to cancel payment:', e);
        }
      }
      
      setTimeout(() => {
        setPaymentStatus(null);
        setCurrentPaymentIntent(null);
      }, 3000);
    }
  };

  // Fallback: Process via Stripe Checkout (opens browser)
  const processInStorePayment = async () => {
    if (posCart.length === 0) return;
    
    try {
      // Create order for in-store purchase
      const order = {
        items: posCart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer_email: 'pos@homelyph.com',
        customer_name: 'In-Store Customer',
        shipping_address: { address: 'In-Store Pickup' },
        origin_url: window.location.origin
      };

      const res = await axios.post(`${API_URL}/api/orders/checkout`, order);
      
      // Open Stripe checkout in new window
      window.open(res.data.checkout_url, '_blank', 'width=500,height=700');
      
      toast.success('Payment window opened');
      setPosCart([]);
      setShowPOSDialog(false);
      
      // Refresh orders after a delay
      setTimeout(fetchData, 5000);
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  // Cancel ongoing payment
  const cancelPayment = async () => {
    if (!terminalRef.current) return;
    
    try {
      await terminalRef.current.cancelCollectPaymentMethod();
      if (currentPaymentIntent) {
        await axios.post(`${API_URL}/api/pos/cancel-payment/${currentPaymentIntent}`, {}, { headers });
      }
      setPaymentStatus(null);
      setCurrentPaymentIntent(null);
      toast.info('Payment cancelled');
    } catch (error) {
      console.error('Cancel failed:', error);
    }
  };

  // Filtering
  const filteredInventory = inventory.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || product.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredPosProducts = inventory.filter(product =>
    product.name.toLowerCase().includes(posSearchQuery.toLowerCase()) &&
    product.in_stock
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-32 bg-gray-100 rounded-xl"></div>
        <div className="animate-pulse h-64 bg-gray-100 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="store-manager">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-2xl font-bold">{summary.total_products || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Healthy Stock</p>
              <p className="text-2xl font-bold text-green-600">{summary.healthy_stock || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Low Stock</p>
              <p className="text-2xl font-bold text-amber-600">{summary.low_stock || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <X className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{summary.out_of_stock || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="h-5 w-5 text-amber-600" />
            <span className="font-medium text-amber-800">Inventory Alerts ({alerts.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.slice(0, 5).map(alert => (
              <span
                key={alert.id}
                className={`px-3 py-1 text-sm rounded-full ${
                  alert.severity === 'critical'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                }`}
              >
                {alert.name}: {alert.stock_quantity} left
              </span>
            ))}
            {alerts.length > 5 && (
              <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700">
                +{alerts.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="inventory">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="inventory" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Package className="h-4 w-4 mr-2" /> Inventory
            </TabsTrigger>
            <TabsTrigger value="orders" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <ClipboardList className="h-4 w-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="history" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <History className="h-4 w-4 mr-2" /> History
            </TabsTrigger>
          </TabsList>
          <Button onClick={() => setShowPOSDialog(true)} className="bg-green-600 hover:bg-green-700" data-testid="pos-btn">
            <Store className="h-4 w-4 mr-2" /> In-Store Sale
          </Button>
        </div>

        {/* Inventory Tab */}
        <TabsContent value="inventory">
          <div className="bg-white rounded-xl border">
            <div className="p-4 border-b flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search products or SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="p-2 border rounded-lg text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Product</th>
                    <th className="px-4 py-3 text-left">SKU</th>
                    <th className="px-4 py-3 text-center">Stock</th>
                    <th className="px-4 py-3 text-center">Threshold</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInventory.map(product => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {product.image_url && (
                            <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            <p className="text-xs text-gray-500">${product.price}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingSku === product.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={newSkuValue}
                              onChange={(e) => setNewSkuValue(e.target.value)}
                              className="w-32 text-sm font-mono"
                              placeholder="Enter SKU"
                              data-testid={`sku-input-${product.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => updateProductSKU(product.id, newSkuValue)}
                              className="bg-green-600 h-8 px-2"
                              data-testid={`save-sku-${product.id}`}
                            >
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={cancelSkuEdit}
                              className="h-8 px-2"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-gray-600">{product.sku || `SKU-${product.id.slice(0, 8).toUpperCase()}`}</span>
                            <button
                              onClick={() => startSkuEdit(product)}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              data-testid={`edit-sku-${product.id}`}
                            >
                              <Edit2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {editingProduct?.id === product.id ? (
                          <Input
                            type="number"
                            value={editingProduct.stock_quantity || 0}
                            onChange={(e) => setEditingProduct({ ...editingProduct, stock_quantity: parseInt(e.target.value) })}
                            className="w-20 text-center"
                          />
                        ) : (
                          <span className={`font-bold ${product.status === 'out_of_stock' ? 'text-red-600' : product.status === 'low_stock' ? 'text-amber-600' : 'text-gray-900'}`}>
                            {product.stock_quantity ?? '∞'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {editingProduct?.id === product.id ? (
                          <Input
                            type="number"
                            value={editingProduct.low_stock_threshold || 5}
                            onChange={(e) => setEditingProduct({ ...editingProduct, low_stock_threshold: parseInt(e.target.value) })}
                            className="w-16 text-center"
                          />
                        ) : (
                          product.low_stock_threshold
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          product.status === 'out_of_stock' ? 'bg-red-100 text-red-700' :
                          product.status === 'low_stock' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {product.status === 'out_of_stock' ? 'Out of Stock' :
                           product.status === 'low_stock' ? 'Low Stock' : 'In Stock'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingProduct?.id === product.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              onClick={() => updateInventory(
                                product.id,
                                editingProduct.stock_quantity,
                                editingProduct.low_stock_threshold,
                                editingProduct.reorder_quantity
                              )}
                              className="bg-green-600"
                            >
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingProduct(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingProduct(product)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingProduct(product);
                                setShowAdjustDialog(true);
                              }}
                            >
                              <ArrowUpDown className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredInventory.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No products found</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <div className="bg-white rounded-xl border">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Recent Orders</h3>
              <span className="text-sm text-gray-500">{orders.length} orders</span>
            </div>
            <div className="divide-y">
              {orders.slice(0, 20).map(order => (
                <div key={order.id} className="hover:bg-gray-50 transition-colors">
                  {/* Order Row - Clickable to expand */}
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    data-testid={`order-row-${order.id.slice(0, 8)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button className="text-gray-400">
                          {expandedOrder === order.id ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <ChevronDown className="h-5 w-5" />
                          )}
                        </button>
                        <div>
                          <p className="font-mono text-sm font-medium">{order.id.slice(0, 8)}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{order.customer_name}</p>
                          <p className="text-xs text-gray-500">{order.customer_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">${order.total?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 
                          order.payment_status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {order.payment_status}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedOrder === order.id && (
                    <div className="bg-gray-50 border-t px-4 py-4" data-testid={`order-details-${order.id.slice(0, 8)}`}>
                      <div className="grid md:grid-cols-3 gap-6">
                        {/* Customer Information */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                            <MapPin className="h-4 w-4" /> Customer Information
                          </h4>
                          <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
                            <p><span className="text-gray-500">Name:</span> {order.customer_name}</p>
                            <p className="flex items-center gap-1">
                              <Mail className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-500">Email:</span> {order.customer_email}
                            </p>
                            {order.customer_phone && (
                              <p className="flex items-center gap-1">
                                <Phone className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-500">Phone:</span> {order.customer_phone}
                              </p>
                            )}
                            {order.shipping_address && (
                              <div className="pt-2 border-t">
                                <p className="text-gray-500 mb-1">Shipping Address:</p>
                                <p>{order.shipping_address.address}</p>
                                {order.shipping_address.city && (
                                  <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
                                )}
                                {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                            <ShoppingCart className="h-4 w-4" /> Order Items
                          </h4>
                          <div className="bg-white rounded-lg p-3 space-y-2">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                                <div>
                                  <p className="font-medium">{item.name}</p>
                                  <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                                </div>
                                <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                              </div>
                            ))}
                            <div className="pt-2 border-t flex justify-between font-bold">
                              <span>Total</span>
                              <span>${order.total?.toFixed(2)}</span>
                            </div>
                            {order.discount_amount > 0 && (
                              <p className="text-xs text-green-600">Discount applied: -${order.discount_amount?.toFixed(2)}</p>
                            )}
                            {order.affiliate_code && (
                              <p className="text-xs text-blue-600">Affiliate: {order.affiliate_code}</p>
                            )}
                          </div>
                        </div>

                        {/* Order Management */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                            <ClipboardList className="h-4 w-4" /> Manage Order
                          </h4>
                          <div className="bg-white rounded-lg p-3 space-y-3">
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">Order Status</label>
                              <select
                                value={order.status}
                                onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                className="w-full text-sm p-2 border rounded-lg"
                                data-testid={`order-status-select-${order.id.slice(0, 8)}`}
                              >
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>Order ID:</span>
                              <code className="bg-gray-100 px-2 py-0.5 rounded font-mono">{order.id}</code>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(order.id);
                                  toast.success('Order ID copied');
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                            {order.tracking_number && (
                              <div className="text-xs">
                                <span className="text-gray-500">Tracking:</span>{' '}
                                <code className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{order.tracking_number}</code>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="space-y-2" data-testid={`order-actions-${order.id.slice(0, 8)}`}>
                            <Button
                              size="sm"
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOrderEmail(order, 'update');
                              }}
                              data-testid={`send-email-btn-${order.id.slice(0, 8)}`}
                            >
                              <Mail className="h-4 w-4 mr-2" /> Send Email Update
                            </Button>
                            {order.status !== 'shipped' && order.status !== 'delivered' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openOrderEmail(order, 'shipped');
                                }}
                                data-testid={`mark-shipped-btn-${order.id.slice(0, 8)}`}
                              >
                                <Truck className="h-4 w-4 mr-2" /> Send Shipping Notice
                              </Button>
                            )}
                            {order.status === 'shipped' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openOrderEmail(order, 'delivered');
                                }}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" /> Send Delivery Confirmation
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {orders.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No orders yet</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="bg-white rounded-xl border">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Inventory Change History</h3>
            </div>
            <div className="divide-y">
              {logs.map(log => (
                <div key={log.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      log.action === 'restock' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {log.action === 'restock' ? (
                        <Plus className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{log.action === 'restock' ? 'Restocked' : 'Adjusted'}</p>
                      <p className="text-sm text-gray-500">
                        {log.previous_quantity !== undefined && (
                          <span>{log.previous_quantity} → {log.new_quantity}</span>
                        )}
                        {log.reason && <span className="ml-2">({log.reason})</span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{log.performed_by}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {logs.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No inventory changes recorded</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Adjust Inventory Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Inventory: {editingProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Current Stock</p>
              <p className="text-3xl font-bold">{editingProduct?.stock_quantity ?? 0}</p>
            </div>
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="lg"
                onClick={() => setAdjustmentData({ ...adjustmentData, quantity: adjustmentData.quantity - 1 })}
              >
                <Minus className="h-5 w-5" />
              </Button>
              <Input
                type="number"
                value={adjustmentData.quantity}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: parseInt(e.target.value) || 0 })}
                className="w-24 text-center text-xl font-bold"
              />
              <Button
                variant="outline"
                size="lg"
                onClick={() => setAdjustmentData({ ...adjustmentData, quantity: adjustmentData.quantity + 1 })}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
            <div className="text-center text-sm text-gray-500">
              New total: {(editingProduct?.stock_quantity ?? 0) + adjustmentData.quantity}
            </div>
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Input
                value={adjustmentData.reason}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                placeholder="e.g., damaged items, found in warehouse"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowAdjustDialog(false);
                setAdjustmentData({ quantity: 0, reason: '' });
              }}>
                Cancel
              </Button>
              <Button onClick={adjustInventory} className="bg-[#003456]">
                Apply Adjustment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* POS / In-Store Sale Dialog */}
      <Dialog open={showPOSDialog} onOpenChange={setShowPOSDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-green-600" />
              In-Store Point of Sale
              {/* Terminal Status Indicator */}
              <span className={`ml-auto flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                terminalStatus === 'connected' ? 'bg-green-100 text-green-700' :
                terminalStatus === 'connecting' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {terminalStatus === 'connected' ? <Wifi className="h-3 w-3" /> : 
                 terminalStatus === 'connecting' ? <Loader2 className="h-3 w-3 animate-spin" /> :
                 <WifiOff className="h-3 w-3" />}
                {terminalStatus === 'connected' ? 'Reader Connected' :
                 terminalStatus === 'connecting' ? 'Connecting...' :
                 'No Reader'}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Reader Connection Section */}
          <div className="border rounded-lg p-4 bg-gray-50 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm text-gray-700">Terminal Reader</h4>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={useSimulator}
                  onChange={(e) => setUseSimulator(e.target.checked)}
                  className="rounded"
                  disabled={posConfigured === false}
                />
                Use Simulator (for testing)
              </label>
            </div>
            
            {/* Stripe Not Configured Warning */}
            {posConfigured === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">Stripe Terminal Not Configured</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  To enable card reader functionality, please add a valid Stripe API key to your backend configuration.
                  You can still use the "Pay via Browser" option below.
                </p>
              </div>
            )}
            
            {connectedReader ? (
              <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{connectedReader.label || 'Card Reader'}</p>
                    <p className="text-xs text-gray-500">{connectedReader.device_type} • {connectedReader.serial_number || 'Simulated'}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={disconnectReader}>
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button 
                    onClick={discoverReaders} 
                    variant="outline" 
                    className="flex-1"
                    disabled={terminalStatus === 'connecting' || posConfigured === false}
                  >
                    {terminalStatus === 'connecting' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Searching...</>
                    ) : (
                      <><Search className="h-4 w-4 mr-2" /> Find Readers</>
                    )}
                  </Button>
                </div>
                
                {discoveredReaders.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 font-medium">Available Readers:</p>
                    {discoveredReaders.map((reader, idx) => (
                      <button
                        key={idx}
                        onClick={() => connectToReader(reader)}
                        className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-white transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <CreditCard className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium text-sm">{reader.label || `Reader ${idx + 1}`}</p>
                            <p className="text-xs text-gray-500">{reader.device_type || 'simulated'}</p>
                          </div>
                        </div>
                        <span className="text-xs text-blue-600">Connect →</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Products */}
            <div>
              <div className="mb-4">
                <Input
                  placeholder="Search products..."
                  value={posSearchQuery}
                  onChange={(e) => setPosSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-[350px] overflow-y-auto">
                {filteredPosProducts.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className="p-3 border rounded-lg text-left hover:bg-gray-50 transition-colors"
                    disabled={paymentStatus}
                  >
                    {product.image_url && (
                      <img src={product.image_url} alt="" className="w-full h-20 object-cover rounded mb-2" />
                    )}
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-green-600 font-bold">${product.price}</p>
                    {product.stock_quantity !== null && (
                      <p className="text-xs text-gray-500">{product.stock_quantity} in stock</p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Cart */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" /> Cart ({posCart.length})
              </h4>
              <div className="space-y-2 max-h-[200px] overflow-y-auto mb-4">
                {posCart.map(item => (
                  <div key={item.id} className="bg-white p-3 rounded-lg flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-green-600">${item.price} x {item.quantity}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => updateCartQuantity(item.id, -1)} disabled={paymentStatus}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="font-bold w-8 text-center">{item.quantity}</span>
                      <Button size="sm" variant="outline" onClick={() => updateCartQuantity(item.id, 1)} disabled={paymentStatus}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {posCart.length === 0 && (
                  <p className="text-center text-gray-400 py-8">No items in cart</p>
                )}
              </div>
              
              <div className="border-t pt-4">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>${getCartTotal().toFixed(2)}</span>
                  </div>
                  {surchargeSettings.enabled && getCartSurcharge() > 0 && (
                    <div className="flex items-center justify-between text-gray-600" data-testid="pos-surcharge">
                      <span className="flex items-center gap-1">
                        <CreditCard className="h-4 w-4" />
                        Card Fee ({surchargeSettings.percent}%)
                      </span>
                      <span>${getCartSurcharge().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-2xl font-bold text-green-600">${getCartTotalWithSurcharge().toFixed(2)}</span>
                  </div>
                </div>
                
                {/* Payment Status Display */}
                {paymentStatus && (
                  <div className={`mb-4 p-4 rounded-lg text-center ${
                    paymentStatus === 'success' ? 'bg-green-100 text-green-700' :
                    paymentStatus === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    {paymentStatus === 'creating' && (
                      <><Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" /><p className="font-medium">Creating payment...</p></>
                    )}
                    {paymentStatus === 'collecting' && (
                      <><CreditCard className="h-8 w-8 mx-auto mb-2 animate-pulse" /><p className="font-medium">Present Card to Reader</p><p className="text-sm mt-1">Waiting for customer...</p></>
                    )}
                    {paymentStatus === 'processing' && (
                      <><Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" /><p className="font-medium">Processing payment...</p></>
                    )}
                    {paymentStatus === 'success' && (
                      <><CheckCircle className="h-8 w-8 mx-auto mb-2" /><p className="font-medium">Payment Successful!</p></>
                    )}
                    {paymentStatus === 'failed' && (
                      <><AlertCircle className="h-8 w-8 mx-auto mb-2" /><p className="font-medium">Payment Failed</p><p className="text-sm mt-1">Please try again</p></>
                    )}
                  </div>
                )}
                
                {/* Payment Buttons */}
                <div className="space-y-2">
                  {connectedReader ? (
                    <>
                      {paymentStatus && paymentStatus !== 'success' && paymentStatus !== 'failed' ? (
                        <Button
                          onClick={cancelPayment}
                          variant="outline"
                          className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <X className="h-4 w-4 mr-2" /> Cancel Payment
                        </Button>
                      ) : (
                        <Button
                          onClick={processTerminalPayment}
                          disabled={posCart.length === 0 || paymentStatus === 'success'}
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid="process-terminal-payment-btn"
                        >
                          <CreditCard className="h-4 w-4 mr-2" /> Pay with Card Reader
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-amber-600 mb-2 flex items-center justify-center gap-1">
                        <AlertTriangle className="h-4 w-4" /> Connect a reader above, or use browser payment
                      </p>
                    </div>
                  )}
                  
                  {/* Fallback to browser checkout */}
                  {!connectedReader && (
                    <Button
                      onClick={processInStorePayment}
                      disabled={posCart.length === 0}
                      variant="outline"
                      className="w-full"
                      data-testid="process-payment-btn"
                    >
                      <CreditCard className="h-4 w-4 mr-2" /> Pay via Browser (Fallback)
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Send Email to Customer
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 pt-2">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">To:</span> {selectedOrder.customer_name}
                </p>
                <p className="text-sm text-gray-500">{selectedOrder.customer_email}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Subject</label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject..."
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Message</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Email message..."
                  rows={8}
                  className="w-full p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="email-message-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEmailModal(false);
                    setSelectedOrder(null);
                    setEmailSubject('');
                    setEmailMessage('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendOrderEmail}
                  disabled={!emailSubject || !emailMessage || sendingEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="send-email-submit"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" /> Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreManager;
